import {
  Box,
  ButtonSelect,
  Link,
  Loader,
  MenuMulti,
  type MenuOption,
} from "@saviynt/design-system";
import { useEffect } from "react";
import { useIntl } from "react-intl";

interface FindingsFilterOptionsProps {
  filterOptions: Record<string, MenuOption[]>;
  setFilterOptions: React.Dispatch<
    React.SetStateAction<Record<string, MenuOption[]>>
  >;
  onClear: () => void;
  data: Array<{ label: string; options: MenuOption[] }>;
  isLoading: boolean;
}

export default function FindingsFilterOptions({
  filterOptions,
  setFilterOptions,
  onClear,
  data,
  isLoading,
}: FindingsFilterOptionsProps) {
  const intl = useIntl();
  useEffect(() => {
    if (data.length > 0) {
      setFilterOptions((prev: Record<string, MenuOption[]>) => {
        const newOptions = { ...prev };
        data.forEach(({ label, options }) => {
          if (!newOptions[label]) {
            newOptions[label] = options;
          } else {
            // Preserve existing isChecked state when updating options
            newOptions[label] = options.map((option) => {
              const existingOption = newOptions[label].find(
                (existing) => existing.value === option.value,
              );
              return {
                ...option,
                isChecked: existingOption?.isChecked || false,
              };
            });
          }
        });
        return newOptions;
      });
    }
  }, [data, setFilterOptions]);

  const handleFilterChange = (label: string, options: MenuOption[]) => {
    setFilterOptions((prev: Record<string, MenuOption[]>) => ({
      ...prev,
      [label]: options,
    }));
  };

  if (isLoading) {
    return (
      <Box className="flex items-center justify-center pt-6">
        <Loader kind="dots" format="inline" color="brand" />
      </Box>
    );
  }

  return (
    <Box className="flex gap-2 items-center flex-wrap pt-6">
      {data.map(({ label }) => (
        <FilterPill
          key={label}
          label={label}
          options={filterOptions[label] || []}
          onOptionsChange={(options) => handleFilterChange(label, options)}
        />
      ))}
      <Link
        text={intl.formatMessage({
          defaultMessage: "Clear",
          id: "3E8xVq",
          description: "Button text to clear date filter selection",
        })}
        kind="button"
        onClick={onClear}
      />
    </Box>
  );
}

const FilterPill = ({
  label,
  options,
  onOptionsChange,
}: {
  label: string;
  options: MenuOption[];
  onOptionsChange: (options: MenuOption[]) => void;
}) => {
  const handleChange = (selectedOptions?: MenuOption[]) => {
    if (!selectedOptions) return;

    const updatedOptions = options.map((option) => ({
      ...option,
      isChecked: selectedOptions.some(
        (selectedOption) => selectedOption.value === option.value,
      ),
    }));

    onOptionsChange(updatedOptions);
  };

  return (
    <MenuMulti
      options={options}
      label={label}
      onChange={handleChange}
      trigger={<ButtonSelect kind="pill" label="" />}
      minOptionsForSearch={1}
      minOptionsForFooter={1}
    />
  );
};
